// Minimal RIFF/WAV writer and reader. The writer makes the 16-bit PCM WAV the audio lab's
// recordings and the Pitch Match "Save last 30 s" upload (issue #3587): mono from one array, or
// several equal-length channels interleaved. The reader takes PCM 8-bit unsigned or 16-bit signed
// with any number of channels: the detector tests read their recorded fixtures with it, and the
// capture replay (scripts/replay-pitch-capture.ts) reads the saved WAV.
// Channel convention for the app's recordings (issue #3708): channel 0 is the microphone, channel 1
// the guide reference (the exact guide audio the app played, sample-aligned with the microphone).
// No DOM access, so the replay runs it in Node.

export interface DecodedWav {
  sampleRate: number;
  samples: Float32Array; // channel 0 (the microphone), so mono readers need not know about channels
  channels: Float32Array[]; // every channel in the file, channel 0 first
}

export function decodeWav(bytes: Uint8Array): DecodedWav {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const tag = (offset: number) => String.fromCharCode(...bytes.subarray(offset, offset + 4));
  if (tag(0) !== 'RIFF' || tag(8) !== 'WAVE') {
    throw new Error('not a WAV file');
  }
  let offset = 12;
  let channelCount = 1;
  let sampleRate = 0;
  let bitsPerSample = 16;
  let channels: Float32Array[] | undefined;
  while (offset + 8 <= bytes.length) {
    const chunkId = tag(offset);
    const chunkSize = view.getUint32(offset + 4, true);
    const body = offset + 8;
    if (chunkId === 'fmt ') {
      channelCount = view.getUint16(body + 2, true);
      if (channelCount < 1) {
        // with no channels every frame is zero bytes long, and a data chunk would hold endlessly many
        throw new Error('WAV fmt chunk declares no channels');
      }
      sampleRate = view.getUint32(body + 4, true);
      bitsPerSample = view.getUint16(body + 14, true);
    } else if (chunkId === 'data') {
      const bytesPerSample = bitsPerSample / 8;
      const frameCount = Math.floor(chunkSize / (bytesPerSample * channelCount));
      channels = Array.from({ length: channelCount }, () => new Float32Array(frameCount));
      for (let i = 0; i < frameCount; i += 1) {
        for (let channel = 0; channel < channelCount; channel += 1) {
          const position = body + (i * channelCount + channel) * bytesPerSample;
          channels[channel][i] =
            bitsPerSample === 8 ? (view.getUint8(position) - 128) / 128 : view.getInt16(position, true) / 32768;
        }
      }
    }
    offset = body + chunkSize + (chunkSize % 2);
  }
  if (!channels || channels.length === 0 || !sampleRate) {
    throw new Error('WAV missing fmt or data chunk');
  }
  return { sampleRate, samples: channels[0], channels };
}

// 16-bit PCM WAV, samples clamped to [-1, 1]. One array writes mono; an array of equal-length
// channels writes them interleaved, channel 0 first.
export function encodeWav(input: Float32Array | Float32Array[], sampleRate: number): ArrayBuffer {
  const channels = Array.isArray(input) ? input : [input];
  if (channels.length === 0) {
    throw new Error('encodeWav needs at least one channel');
  }
  const frameCount = channels[0].length;
  if (channels.some((channel) => channel.length !== frameCount)) {
    const lengths = channels.map((channel) => channel.length).join(', ');
    throw new Error(`encodeWav channels must be the same length (got ${lengths})`);
  }
  const bytesPerSample = 2;
  const blockAlign = bytesPerSample * channels.length;
  const dataSize = frameCount * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, channels.length, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * blockAlign, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, dataSize, true);
  let offset = 44;
  for (let i = 0; i < frameCount; i += 1) {
    for (const channel of channels) {
      const clamped = Math.max(-1, Math.min(1, channel[i]));
      view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
      offset += bytesPerSample;
    }
  }
  return buffer;
}
